import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { X, Building2, User, Mail, Phone, MapPin, FileText, Calendar, Wrench } from 'lucide-react';
import { clientsAPI, clientFlagsAPI } from '@shared/apiClient';
import { FlagIcon } from '@shared/flagIcons';
import toast from 'react-hot-toast';

export default function ClientFormModal({ client, isOpen, onClose, mode = 'create' }) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    companyName: '',
    contactFirstName: '',
    contactLastName: '',
    email: '',
    phone: '',
    secondPhone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    notes: '',
    flagIds: [],
    isActive: true,
    isContractClient: false,
    isCommercial: false,
    omniMatrix: false,
    omniMatrixId: '',
    gateCode: false,
    gateCodeValue: '',
    serviceCycleMonths: [null, null],
    oilChangeMonth: null,
    generatorType: '',
    generatorModel: '',
    generatorSerialNumber: '',
    generatorSize: '',
    generatorExerciseTime: '',
    airFilter: '',
    fuelFilter1: '',
    fuelFilter2: '',
    oilFilter1: '',
    oilFilter2: '',
    fuelAdditive: '',
    fuelType: '',
    oilType: '',
    oilAmount: '',
    batterySize: '',
    batteryDateCode: '',
    batteryDateChanged: ''
  });
  const [errors, setErrors] = useState({});
  const { data: flagsData } = useQuery(
    'clientFlags',
    () => clientFlagsAPI.getClientFlags({ includeInactive: true }),
    { enabled: isOpen }
  );

  // Initialize form data when client changes
  useEffect(() => {
    if (!isOpen) return; // Don't update if modal is closed
    
    if (client && mode === 'edit') {
      setFormData({
        companyName: client.companyName ?? '',
        contactFirstName: client.contactFirstName ?? '',
        contactLastName: client.contactLastName ?? '',
        email: client.email ?? '',
        phone: client.phone ?? '',
        secondPhone: client.secondPhone ?? '',
        address: client.address ?? '',
        city: client.city ?? '',
        state: client.state ?? '',
        zipCode: client.zipCode ?? '',
        notes: client.notes ?? '',
        flagIds: client.flags?.map(flag => flag.id) || [],
        isActive: client.isActive !== undefined ? client.isActive : true,
        isContractClient: client.isContractClient ?? false,
        isCommercial: client.isCommercial ?? false,
        omniMatrix: client.omniMatrix ?? false,
        omniMatrixId: client.omniMatrixId ?? '',
        gateCode: client.gateCode ?? false,
        gateCodeValue: client.gateCodeValue ?? '',
        serviceCycleMonths: client.serviceCycleMonths || [null, null],
        oilChangeMonth: client.oilChangeMonth ?? null,
        generatorType: client.generatorType ?? '',
        generatorModel: client.generatorModel ?? '',
        generatorSerialNumber: client.generatorSerialNumber ?? '',
        generatorSize: client.generatorSize ?? '',
        generatorExerciseTime: client.generatorExerciseTime ?? '',
        airFilter: client.airFilter ?? '',
        fuelFilter1: client.fuelFilter1 ?? '',
        fuelFilter2: client.fuelFilter2 ?? '',
        oilFilter1: client.oilFilter1 ?? '',
        oilFilter2: client.oilFilter2 ?? '',
        fuelAdditive: client.fuelAdditive ?? '',
        fuelType: client.fuelType ?? '',
        oilType: client.oilType ?? '',
        oilAmount: client.oilAmount ?? '',
        batterySize: client.batterySize ?? '',
        batteryDateCode: client.batteryDateCode ?? '',
        batteryDateChanged: client.batteryDateChanged ? new Date(client.batteryDateChanged).toISOString().split('T')[0] : ''
      });
      setErrors({});
    } else if (mode === 'create') {
      // Reset form for create mode
      setFormData({
        companyName: '',
        contactFirstName: '',
        contactLastName: '',
        email: '',
        phone: '',
        secondPhone: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        notes: '',
        flagIds: [],
        isActive: true,
        isContractClient: false,
        isCommercial: false,
        omniMatrix: false,
        omniMatrixId: '',
        gateCode: false,
        gateCodeValue: '',
        serviceCycleMonths: [null, null],
        oilChangeMonth: null,
        generatorType: '',
        generatorModel: '',
        generatorSerialNumber: '',
        generatorSize: '',
        generatorExerciseTime: '',
        airFilter: '',
        fuelFilter1: '',
        fuelFilter2: '',
        oilFilter1: '',
        oilFilter2: '',
        fuelAdditive: '',
        fuelType: '',
        oilType: '',
        oilAmount: '',
        batterySize: '',
        batteryDateCode: '',
        batteryDateChanged: ''
      });
      setErrors({});
    }
  }, [client, mode, isOpen]);

  // Create mutation
  const createMutation = useMutation(
    (data) => clientsAPI.createClient(data),
    {
      onSuccess: () => {
        toast.success('Customer created successfully!');
        queryClient.invalidateQueries('clients');
        onClose();
      },
      onError: (error) => {
        const errorData = error.response?.data;
        if (errorData?.errors) {
          const fieldErrors = {};
          errorData.errors.forEach(err => {
            if (err.path) {
              fieldErrors[err.path] = err.msg;
            }
          });
          setErrors(fieldErrors);
        } else {
          toast.error(errorData?.message || 'Failed to create customer');
        }
      }
    }
  );

  // Update mutation
  const updateMutation = useMutation(
    (data) => clientsAPI.updateClient(client.id, data),
    {
      onSuccess: () => {
        toast.success('Customer updated successfully!');
        queryClient.invalidateQueries('clients');
        queryClient.invalidateQueries('allClients');
        queryClient.invalidateQueries('contractClientsNeedingService');
        queryClient.invalidateQueries('weeklyAssignments');
        queryClient.invalidateQueries(['client', client.id]);
        onClose();
      },
      onError: (error) => {
        const errorData = error.response?.data;
        if (errorData?.errors) {
          const fieldErrors = {};
          errorData.errors.forEach(err => {
            if (err.path) {
              fieldErrors[err.path] = err.msg;
            }
          });
          setErrors(fieldErrors);
        } else {
          toast.error(errorData?.message || 'Failed to update customer');
        }
      }
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    () => clientsAPI.deactivateClient(client.id),
    {
      onSuccess: () => {
        toast.success('Customer deactivated successfully!');
        queryClient.invalidateQueries('clients');
        onClose();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to deactivate customer');
      }
    }
  );

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleFlagToggle = (flagId) => {
    setFormData(prev => {
      const hasFlag = prev.flagIds.includes(flagId);
      return {
        ...prev,
        flagIds: hasFlag
          ? prev.flagIds.filter(id => id !== flagId)
          : [...prev.flagIds, flagId]
      };
    });
  };

  const handleServiceCycleChange = (index, value) => {
    const months = [...formData.serviceCycleMonths];
    months[index] = value ? parseInt(value) : null;
    setFormData(prev => ({
      ...prev,
      serviceCycleMonths: months
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});

    // Prepare data - convert empty strings to null for optional fields
    // Filter out null values from serviceCycleMonths to support 1 or 2 months
    const filteredServiceMonths = formData.isContractClient && formData.serviceCycleMonths
      ? formData.serviceCycleMonths.filter(m => m !== null)
      : [];
    
    const submitData = {
      companyName: formData.isCommercial ? (formData.companyName?.trim() || null) : null,
      contactFirstName: formData.contactFirstName?.trim(),
      contactLastName: formData.contactLastName?.trim(),
      email: formData.email?.trim() || null,
      phone: formData.phone?.trim() || null,
      secondPhone: formData.secondPhone?.trim() || null,
      address: formData.address?.trim(),
      city: formData.city?.trim() || null,
      state: formData.state?.trim() || null,
      zipCode: formData.zipCode?.trim() || null,
      notes: formData.notes?.trim() || null,
      flagIds: formData.flagIds,
      isActive: formData.isActive,
      isCommercial: formData.isCommercial || false,
      isContractClient: formData.isContractClient || false,
      omniMatrix: formData.omniMatrix || false,
      omniMatrixId: formData.omniMatrix ? formData.omniMatrixId?.trim() || null : null,
      gateCode: formData.gateCode || false,
      gateCodeValue: formData.gateCode ? formData.gateCodeValue?.trim() || null : null,
      serviceCycleMonths: filteredServiceMonths.length > 0 ? filteredServiceMonths : null,
      oilChangeMonth: formData.isContractClient && formData.oilChangeMonth
        ? formData.oilChangeMonth
        : null,
      generatorType: formData.generatorType?.trim() || null,
      generatorModel: formData.generatorModel?.trim() || null,
      generatorSerialNumber: formData.generatorSerialNumber?.trim() || null,
      generatorSize: formData.generatorSize?.trim() || null,
      generatorExerciseTime: formData.generatorExerciseTime?.trim() || null,
      airFilter: formData.airFilter?.trim() || null,
      fuelFilter1: formData.fuelFilter1?.trim() || null,
      fuelFilter2: formData.fuelFilter2?.trim() || null,
      oilFilter1: formData.oilFilter1?.trim() || null,
      oilFilter2: formData.oilFilter2?.trim() || null,
      fuelAdditive: formData.fuelAdditive?.trim() || null,
      fuelType: formData.fuelType?.trim() || null,
      oilType: formData.oilType?.trim() || null,
      oilAmount: formData.oilAmount?.trim() || null,
      batterySize: formData.batterySize?.trim() || null,
      batteryDateCode: formData.batteryDateCode?.trim() || null,
      batteryDateChanged: formData.batteryDateChanged || null
    };

    if (mode === 'create') {
      createMutation.mutate(submitData);
    } else {
      updateMutation.mutate(submitData);
    }
  };

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to deactivate ${client.companyName}? This action can be undone.`)) {
      deleteMutation.mutate();
    }
  };

  if (!isOpen) return null;

  const isLoading = createMutation.isLoading || updateMutation.isLoading || deleteMutation.isLoading;
  const monthOptions = [
    { value: '', label: 'Select month' },
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="bg-gray-50 px-4 py-5 sm:px-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  {mode === 'create' ? 'Create New Customer' : 'Edit Customer'}
                </h3>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="px-4 py-5 sm:p-6 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {/* Is Commercial Client - moved to top */}
                <div className="sm:col-span-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="isCommercial"
                      checked={formData.isCommercial}
                      onChange={handleChange}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">Commercial Customer</span>
                  </label>
                  <p className="mt-1 text-xs text-gray-500">Check this if the client is a commercial entity (company/business)</p>
                </div>

                {/* Company Name - Only show for commercial clients */}
                {formData.isCommercial && (
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Building2 className="inline h-4 w-4 mr-1" />
                      Company Name
                    </label>
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        errors.companyName ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.companyName && (
                      <p className="mt-1 text-sm text-red-600">{errors.companyName}</p>
                    )}
                  </div>
                )}

                {/* Contact First Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <User className="inline h-4 w-4 mr-1" />
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="contactFirstName"
                    value={formData.contactFirstName}
                    onChange={handleChange}
                    required
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.contactFirstName ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.contactFirstName && (
                    <p className="mt-1 text-sm text-red-600">{errors.contactFirstName}</p>
                  )}
                </div>

                {/* Contact Last Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="contactLastName"
                    value={formData.contactLastName}
                    onChange={handleChange}
                    required
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.contactLastName ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.contactLastName && (
                    <p className="mt-1 text-sm text-red-600">{errors.contactLastName}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Mail className="inline h-4 w-4 mr-1" />
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Phone className="inline h-4 w-4 mr-1" />
                    Phone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.phone ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                  )}
                </div>

                {/* Secondary Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Phone className="inline h-4 w-4 mr-1" />
                    Secondary Phone
                  </label>
                  <input
                    type="tel"
                    name="secondPhone"
                    value={formData.secondPhone}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.secondPhone ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.secondPhone && (
                    <p className="mt-1 text-sm text-red-600">{errors.secondPhone}</p>
                  )}
                </div>

                {/* Address */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MapPin className="inline h-4 w-4 mr-1" />
                    Address *
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.address ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.address && (
                    <p className="mt-1 text-sm text-red-600">{errors.address}</p>
                  )}
                </div>

                {/* City */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.city ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.city && (
                    <p className="mt-1 text-sm text-red-600">{errors.city}</p>
                  )}
                </div>

                {/* State */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    maxLength={2}
                    placeholder="TX"
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 uppercase ${
                      errors.state ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.state && (
                    <p className="mt-1 text-sm text-red-600">{errors.state}</p>
                  )}
                </div>

                {/* Zip Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Zip Code
                  </label>
                  <input
                    type="text"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.zipCode ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.zipCode && (
                    <p className="mt-1 text-sm text-red-600">{errors.zipCode}</p>
                  )}
                </div>

                {/* Notes */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FileText className="inline h-4 w-4 mr-1" />
                    Notes
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      errors.notes ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.notes && (
                    <p className="mt-1 text-sm text-red-600">{errors.notes}</p>
                  )}
                </div>

                {/* Customer Flags */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Customer Flags
                  </label>
                  <div className="border border-gray-200 rounded-md p-3">
                    {flagsData?.data?.flags?.length ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {flagsData.data.flags.map((flag) => {
                          const isSelected = formData.flagIds.includes(flag.id);
                          return (
                            <label
                              key={flag.id}
                              className={`flex items-center gap-2 rounded-md border px-2 py-2 text-sm cursor-pointer transition-colors ${
                                isSelected
                                  ? 'border-blue-400 bg-blue-50 text-blue-700'
                                  : 'border-gray-200 text-gray-700 hover:border-blue-300'
                              } ${flag.isActive ? '' : 'opacity-60'}`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleFlagToggle(flag.id)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <FlagIcon
                                iconName={flag.icon}
                                className="h-4 w-4 text-gray-600"
                                title={flag.name}
                              />
                              <span>
                                {flag.name}
                                {!flag.isActive && <span className="text-xs text-gray-500"> (Inactive)</span>}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">
                        No flags created yet. Add flags in Advanced Settings.
                      </p>
                    )}
                  </div>
                </div>

                {/* Access Information */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Access Information
                  </label>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="omniMatrix"
                          checked={formData.omniMatrix}
                          onChange={handleChange}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Omni Matrix</span>
                      </label>
                      {formData.omniMatrix && (
                        <input
                          type="text"
                          name="omniMatrixId"
                          value={formData.omniMatrixId}
                          onChange={handleChange}
                          placeholder="Omni Matrix ID"
                          className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                            errors.omniMatrixId ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                      )}
                      {errors.omniMatrixId && (
                        <p className="text-sm text-red-600">{errors.omniMatrixId}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="gateCode"
                          checked={formData.gateCode}
                          onChange={handleChange}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Gate Code Required</span>
                      </label>
                      {formData.gateCode && (
                        <input
                          type="text"
                          name="gateCodeValue"
                          value={formData.gateCodeValue}
                          onChange={handleChange}
                          placeholder="Gate code"
                          className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                            errors.gateCodeValue ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                      )}
                      {errors.gateCodeValue && (
                        <p className="text-sm text-red-600">{errors.gateCodeValue}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Is Active */}
                {mode === 'edit' && (
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleChange}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Active</span>
                    </label>
                  </div>
                )}

                {/* Is Contract Client */}
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="isContractClient"
                      checked={formData.isContractClient}
                      onChange={handleChange}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Contract Customer</span>
                  </label>
                </div>

                {/* Service Cycle Months */}
                {formData.isContractClient && (
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Calendar className="inline h-4 w-4 mr-1" />
                      Service Cycle Months (Select 1 or 2 months)
                    </label>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <select
                          value={formData.serviceCycleMonths[0] || ''}
                          onChange={(e) => handleServiceCycleChange(0, e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        >
                          {monthOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <select
                          value={formData.serviceCycleMonths[1] || ''}
                          onChange={(e) => handleServiceCycleChange(1, e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        >
                          {monthOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {/* Oil Change Month Selector */}
                    {formData.serviceCycleMonths[0] && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Oil Change Month (Optional)
                        </label>
                        <select
                          value={formData.oilChangeMonth || ''}
                          onChange={(e) => setFormData({ ...formData, oilChangeMonth: e.target.value ? parseInt(e.target.value) : null })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">None selected</option>
                          {formData.serviceCycleMonths.map((month, index) => {
                            if (!month) return null;
                            const monthOption = monthOptions.find(opt => opt.value === String(month));
                            return (
                              <option key={index} value={month}>
                                {monthOption ? monthOption.label : `Month ${month}`}
                              </option>
                            );
                          })}
                        </select>
                        <p className="mt-1 text-xs text-gray-500">
                          Select which service month requires an oil change (if any)
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Generator Information */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Wrench className="inline h-4 w-4 mr-1" />
                    Generator Information (Optional)
                  </label>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Type
                      </label>
                      <input
                        type="text"
                        name="generatorType"
                        value={formData.generatorType}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., Standby, Portable"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Model
                      </label>
                      <input
                        type="text"
                        name="generatorModel"
                        value={formData.generatorModel}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., Model XYZ"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Serial Number
                      </label>
                      <input
                        type="text"
                        name="generatorSerialNumber"
                        value={formData.generatorSerialNumber}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Serial number"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Size
                      </label>
                      <input
                        type="text"
                        name="generatorSize"
                        value={formData.generatorSize}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., 50kW, 100kW"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Exercise Time
                      </label>
                      <input
                        type="text"
                        name="generatorExerciseTime"
                        value={formData.generatorExerciseTime}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., 2:00 PM"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Air Filter
                      </label>
                      <input
                        type="text"
                        name="airFilter"
                        value={formData.airFilter}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Part number or description"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Fuel Filter 1
                      </label>
                      <input
                        type="text"
                        name="fuelFilter1"
                        value={formData.fuelFilter1}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Part number or description"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Fuel Filter 2
                      </label>
                      <input
                        type="text"
                        name="fuelFilter2"
                        value={formData.fuelFilter2}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Part number or description"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Oil Filter 1
                      </label>
                      <input
                        type="text"
                        name="oilFilter1"
                        value={formData.oilFilter1}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Part number or description"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Oil Filter 2
                      </label>
                      <input
                        type="text"
                        name="oilFilter2"
                        value={formData.oilFilter2}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Part number or description"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Fuel Additive
                      </label>
                      <input
                        type="text"
                        name="fuelAdditive"
                        value={formData.fuelAdditive}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Type or brand"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Fuel Type
                      </label>
                      <input
                        type="text"
                        name="fuelType"
                        value={formData.fuelType}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., Diesel, Gasoline, Natural Gas"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Oil Type
                      </label>
                      <input
                        type="text"
                        name="oilType"
                        value={formData.oilType}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., 15W-40, Synthetic"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Oil Amount
                      </label>
                      <input
                        type="text"
                        name="oilAmount"
                        value={formData.oilAmount}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., 5 quarts, 10 liters"
                      />
                    </div>
                  </div>
                </div>

                {/* Battery Information */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Wrench className="inline h-4 w-4 mr-1" />
                    Battery Information (Optional)
                  </label>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Battery Size
                      </label>
                      <input
                        type="text"
                        name="batterySize"
                        value={formData.batterySize}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., Group 24, 35"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Date Code (MM/YY)
                      </label>
                      <input
                        type="text"
                        name="batteryDateCode"
                        value={formData.batteryDateCode}
                        onChange={handleChange}
                        maxLength={5}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="MM/YY"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Date Changed
                      </label>
                      <input
                        type="date"
                        name="batteryDateChanged"
                        value={formData.batteryDateChanged}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-4 py-3 sm:px-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                {/* Left side - Deactivate button */}
                <div>
                  {mode === 'edit' && (
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={isLoading}
                      className="inline-flex justify-center rounded-md border border-red-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-red-700 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Deactivate
                    </button>
                  )}
                </div>
                
                {/* Right side - Cancel and Save buttons */}
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Saving...' : mode === 'create' ? 'Create Customer' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}








